import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Lock, 
  Eye, 
  CreditCard, 
  Cookie, 
  Trash2, 
  UserCheck, 
  Globe, 
  Mail, 
  FileText, 
  AlertCircle,
  Database,
  Server,
  ChevronRight,
  Sparkles
} from 'lucide-react';
import { LandingHeader } from './LandingHeader';
import LandingFooter from './LandingFooter';

interface NavSection {
  id: string;
  title: string;
  icon: React.ElementType;
}

const sections: NavSection[] = [
  { id: 'intro', title: '1. Introduction', icon: FileText },
  { id: 'collect', title: '2. Information We Collect', icon: Database },
  { id: 'use', title: '3. Why We Collect Information', icon: Sparkles },
  { id: 'verification', title: '4. User Verification', icon: UserCheck },
  { id: 'visibility', title: '5. Data Sharing & Visibility', icon: Eye },
  { id: 'payment', title: '6. Payment Information', icon: CreditCard },
  { id: 'security', title: '7. Password Security & Safeguards', icon: Lock },
  { id: 'cookies', title: '8. Cookies & Tracking', icon: Cookie },
  { id: 'retention', title: '9. Data Retention & Deletion', icon: Trash2 },
  { id: 'third-party', title: '10. Third-Party Services', icon: Server },
  { id: 'rights', title: '11. Your Rights & Compliance', icon: ShieldCheck },
  { id: 'contact', title: '12. Legal & Contact Info', icon: Globe },
];

export const PrivacyPolicy: React.FC = () => {
  const [activeSection, setActiveSection] = useState<string>('intro');

  const scrollToSection = (id: string) => {
    setActiveSection(id);
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 text-slate-300 font-sans selection:bg-cyan-500 selection:text-slate-950 relative overflow-hidden">
      <LandingHeader/>
      {/* Glow Effects */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[350px] bg-cyan-500/10 blur-[140px] pointer-events-none rounded-full" />
      <div className="absolute top-1/3 right-0 w-[400px] h-[400px] bg-blue-600/10 blur-[150px] pointer-events-none rounded-full" />

      {/* Header Banner */}
      <header className="border-b border-slate-800 bg-slate-900/60 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <span className="px-2.5 py-0.5 text-xs font-mono font-medium bg-cyan-950 text-cyan-400 border border-cyan-800/50 rounded-full tracking-wide">
                NEXGENT SECURITY & PRIVACY
              </span>
              <span className="text-xs text-slate-500">v2.4 • Effective Aug 2026</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-white tracking-tight">
              Privacy Policy & Data Architecture
            </h1>
          </div>
          <div className="flex items-center gap-3 text-xs font-mono text-slate-400 bg-slate-900/80 px-3 py-2 rounded-lg border border-slate-800">
            <ShieldCheck className="w-4 h-4 text-cyan-400" />
            <span>DPDP Act (India) & Global Compliant</span>
          </div>
        </div>
      </header>

      {/* Main Container */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-1 lg:grid-cols-12 gap-8">
        
        {/* Navigation Sidebar */}
        <aside className="lg:col-span-4 xl:col-span-3">
          <div className="sticky top-24 bg-slate-900/70 border border-slate-800 rounded-xl p-4 backdrop-blur-md shadow-xl">
            <h2 className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-3 px-2">
              Policy Index
            </h2>
            <nav className="space-y-1">
              {sections.map((item) => {
                const Icon = item.icon;
                const isActive = activeSection === item.id;
                return (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center justify-between px-3 py-2.5 text-xs font-medium rounded-lg transition-all text-left ${
                      isActive
                        ? 'bg-cyan-950/60 text-cyan-300 border border-cyan-800/60 shadow-[0_0_15px_rgba(6,182,212,0.15)]'
                        : 'text-slate-400 hover:text-slate-200 hover:bg-slate-800/50'
                    }`}
                  >
                    <div className="flex items-center gap-2.5 truncate">
                      <Icon className={`w-4 h-4 shrink-0 ${isActive ? 'text-cyan-400' : 'text-slate-500'}`} />
                      <span className="truncate">{item.title}</span>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 transition-transform ${isActive ? 'text-cyan-400 translate-x-0.5' : 'text-slate-600'}`} />
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        {/* Policy Content */}
        <div className="lg:col-span-8 xl:col-span-9 space-y-12">
          
          {/* Section 1: Intro */}
          <section id="intro" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <FileText className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">1. Introduction</h2>
            </div>
            <p className="text-slate-300 leading-relaxed text-sm md:text-base">
              Welcome to <strong className="text-cyan-400">Nexgent</strong> (operated by Motherson Infotech). Nexgent is an executive leadership hiring platform designed to securely connect top-tier talent with validated corporate recruiters. Your privacy and the confidentiality of your career trajectories are fundamental to our infrastructure design.
            </p>
            <p className="text-slate-400 text-sm leading-relaxed">
              This Privacy Policy outlines how we collect, process, encrypt, store, and protect your personal and professional data. By accessing or using the platform, you agree to the protocols detailed herein.
            </p>
          </section>

          {/* Section 2: Info Collect */}
          <section id="collect" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Database className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">2. Information We Collect</h2>
            </div>
            <p className="text-slate-400 text-sm">We collect distinct tiers of information to power our matching and verification infrastructure:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
              <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-2">
                <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span> Personal Identification
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">Full name, email address, phone number, LinkedIn URL, geographic location, and executive resume file artifacts.</p>
              </div>

              <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-2">
                <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span> Professional & Work History
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">Employment history, compensation requirements, leadership domains, verified skills, and career intent metrics.</p>
              </div>

              <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-2">
                <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span> Credentials & Technical Data
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">Hashed passwords, IP address, user-agent profiles, browser type, device metadata, and session timestamps.</p>
              </div>

              <div className="bg-slate-950/60 p-4 rounded-xl border border-slate-800 space-y-2">
                <h3 className="text-sm font-semibold text-cyan-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 bg-cyan-400 rounded-full"></span> Billing Records
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">Transaction IDs, plan metadata, invoice records, and tax identifiers (processed safely via Razorpay).</p>
              </div>
            </div>
          </section>

          {/* Section 3: Why We Collect */}
          <section id="use" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Sparkles className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">3. Why We Collect Information</h2>
            </div>
            <ul className="space-y-3 text-sm text-slate-300">
              {[
                { title: 'Identity & Authentication:', desc: 'Provisioning accounts, continuous session state security, and MFA checks.' },
                { title: 'Reverse Matching Engine:', desc: 'Enabling vetted corporate recruiters to evaluate executive profile parameters against open leadership roles.' },
                { title: 'Subscription Management:', desc: 'Facilitating recurring tier upgrades, processing invoicing, and issuing statutory compliance documents.' },
                { title: 'Fraud & Abuse Prevention:', desc: 'Continuous threat analysis, automated bot protection, and unauthorized scrapers suppression.' },
              ].map((item, idx) => (
                <li key={idx} className="flex items-start gap-3 bg-slate-950/40 p-3 rounded-lg border border-slate-800/50">
                  <span className="text-cyan-400 font-mono text-xs mt-0.5">0{idx + 1}.</span>
                  <span><strong className="text-white">{item.title}</strong> {item.desc}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 4: Verification */}
          <section id="verification" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <UserCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">4. User Verification Process</h2>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              To uphold high network integrity, Nexgent enforces strict multi-step validation checks before granting recruiter or candidate access:
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-2">
              <div className="p-4 bg-slate-950/80 rounded-xl border border-slate-800 text-center space-y-2">
                <div className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Stage 1</div>
                <div className="text-sm font-semibold text-white">OTP Verification</div>
                <div className="text-xs text-slate-400">One-Time Passcode validations sent via SMS/Email to secure communication lines.</div>
              </div>
              <div className="p-4 bg-slate-950/80 rounded-xl border border-slate-800 text-center space-y-2">
                <div className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Stage 2</div>
                <div className="text-sm font-semibold text-white">Corporate Identity</div>
                <div className="text-xs text-slate-400">Verification of domain emails, LinkedIn identities, and corporate registry details for recruiters.</div>
              </div>
              <div className="p-4 bg-slate-950/80 rounded-xl border border-slate-800 text-center space-y-2">
                <div className="text-xs font-mono text-cyan-400 uppercase tracking-widest">Stage 3</div>
                <div className="text-sm font-semibold text-white">Manual Audit</div>
                <div className="text-xs text-slate-400">Human review by administrative team for high-level executive credentials.</div>
              </div>
            </div>
          </section>

          {/* Section 5: Visibility */}
          <section id="visibility" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Eye className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">5. Data Sharing & Visibility</h2>
            </div>
            <div className="space-y-4 text-sm text-slate-300">
              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2">
                <h3 className="font-semibold text-white flex items-center justify-between">
                  <span>What Recruiter Accounts Can Access</span>
                  <span className="text-xs text-cyan-400 font-mono">Restricted View</span>
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Verified recruiters can evaluate work history, technical stack expertise, anonymized metrics, and target compensation metrics.
                </p>
              </div>

              <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2">
                <h3 className="font-semibold text-white flex items-center justify-between">
                  <span>Confidential Information</span>
                  <span className="text-xs text-red-400 font-mono">Strictly Protected</span>
                </h3>
                <p className="text-xs text-slate-400 leading-relaxed">
                  Direct personal phone numbers, primary email addresses, and unmasked current employer identifiers remain protected until you explicitly authorize a connection or accept an inquiry.
                </p>
              </div>
            </div>
          </section>

          {/* Section 6: Payment */}
          <section id="payment" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <CreditCard className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">6. Payment Processing</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              All financial transactions are safely routed through PCI-DSS Level 1 compliant gateway partners (<strong className="text-white">Razorpay</strong>).
            </p>
            <div className="p-4 bg-cyan-950/20 border border-cyan-800/40 rounded-xl text-xs text-slate-300 space-y-1">
              <p className="font-semibold text-cyan-300">Zero Local Storage policy for Payment Credentials:</p>
              <p className="text-slate-400">Nexgent servers never store, process, or transmit raw credit/debit card numbers, CVVs, or NetBanking credentials. We only retain tokenized transaction signatures and compliance metadata for accounting records.</p>
            </div>
          </section>

          {/* Section 7: Security & Passwords */}
          <section id="security" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Lock className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">7. Password Security & Technical Controls</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
              <div className="p-4 bg-slate-950/80 rounded-xl border border-slate-800 space-y-2">
                <div className="font-semibold text-cyan-400">BCrypt Password Hashing</div>
                <div className="text-slate-400">Passwords are never saved in plain text. We utilize industry-standard salted hashing algorithms before persistence.</div>
              </div>
              <div className="p-4 bg-slate-950/80 rounded-xl border border-slate-800 space-y-2">
                <div className="font-semibold text-cyan-400">Encryption in Transit & At Rest</div>
                {/* <div className="text-slate-400">All browser-to-server traffic is forced via TLS 1.3 encryption. Storage volumes are encrypted using AES-256.</div> */}

                <div className="text-slate-400"> All browser-to-server traffic is protected using HTTPS and modern TLS encryption, helping safeguard data transmitted between users and the Nexgent platform.</div>
              </div>
            </div>
          </section>

          {/* Section 8: Cookies */}
          <section id="cookies" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Cookie className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">8. Cookies & Telemetry</h2>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              We leverage essential cookies and lightweight performance analytics tools to maintain state and refine user experience.
            </p>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between items-center p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <span className="text-white font-medium">Essential Tokens (JWT/Session)</span>
                <span className="text-cyan-400 font-mono">Strictly Necessary</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <span className="text-white font-medium">Performance Metrics (Google Analytics)</span>
                <span className="text-slate-400 font-mono">Anonymized / Optional</span>
              </div>
            </div>
          </section>

          {/* Section 9: Retention & Deletion */}
          <section id="retention" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Trash2 className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">9. Data Retention & Deletion Rights</h2>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed">
              You retain total ownership over your personal data lifecycle. You may request account deletion at any point via your profile settings or by sending a request to our data protection team.
            </p>
            <div className="p-4 bg-slate-950/80 border border-slate-800 rounded-xl space-y-2 text-xs">
              <div className="text-cyan-400 font-semibold">Account Erasure Timeline</div>
              <p className="text-slate-400">Upon an erasure confirmation, account parameters and uploaded resumes are purged from active production databases within <strong className="text-white">30 business days</strong>. Tax invoices and historical ledger records are kept for statutory compliance as mandated by law.</p>
            </div>
          </section>

          {/* Section 10: Third-Party */}
          <section id="third-party" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <Server className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">10. Third-Party Infrastructure & Global Transfers</h2>
            </div>
            <p className="text-slate-400 text-sm leading-relaxed">
              We rely on trusted third-party providers operating under data processing agreements:
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-center text-xs">
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <div className="font-semibold text-white">Razorpay</div>
                <div className="text-slate-500 text-[10px] mt-1">Payment Infrastructure</div>
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <div className="font-semibold text-white">Hostinger</div>
                <div className="text-slate-500 text-[10px] mt-1">Application Hosting & Infrastructure</div>
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <div className="font-semibold text-white">ZeptoMail</div>
                <div className="text-slate-500 text-[10px] mt-1">Transactional Emails</div>
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <div className="font-semibold text-white">Google Analytics</div>
                <div className="text-slate-500 text-[10px] mt-1">Website Analytics & Performance Monitoring</div>
              </div>
            </div>
          </section>

          {/* Section 11: Rights & Compliance */}
          <section id="rights" className="scroll-mt-28 space-y-4 bg-slate-900/40 p-6 md:p-8 rounded-2xl border border-slate-800/80">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-slate-800 text-cyan-400 rounded-lg border border-slate-700">
                <ShieldCheck className="w-5 h-5" />
              </div>
              <h2 className="text-xl font-bold text-white">11. Rights, Minors & Compliance</h2>
            </div>
            <div className="space-y-3 text-xs text-slate-300">
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <strong className="text-white block mb-1">Applicable Regulations</strong>
                We adhere to the <strong className="text-cyan-400">Information Technology Act, 2000</strong> and the <strong className="text-cyan-400">Digital Personal Data Protection Act (DPDP Act)</strong> of India.
              </div>
              <div className="p-3 bg-slate-950/60 rounded-lg border border-slate-800">
                <strong className="text-white block mb-1">Age Restrictions (18+)</strong>
                Nexgent is designed strictly for adult working professionals aged 18 and above. We do not knowingly collect personal information from minors.
              </div>
            </div>
          </section>

          {/* Section 12: Contact */}
          <section id="contact" className="scroll-mt-28 space-y-6 bg-slate-900/80 p-6 md:p-8 rounded-2xl border border-cyan-800/50 shadow-[0_0_30px_rgba(6,182,212,0.1)]">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-cyan-950 text-cyan-400 rounded-lg border border-cyan-800">
                <Globe className="w-5 h-5" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">12. Corporate & Data Protection Contact</h2>
                <p className="text-xs text-slate-400">Reach out to our Data Privacy Officer for questions or requests</p>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">ENTITY NAME</div>
                <div className="text-sm font-bold text-white">Motherson Infotech</div>
                <div className="text-slate-400">Nexgent Tech Division</div>
              </div>

              <div className="p-4 bg-slate-950 rounded-xl border border-slate-800 space-y-1">
                <div className="text-slate-500 font-mono">PRIVACY & LEGAL INQUIRIES</div>
                <div className="text-sm font-bold text-cyan-400">privacy@nexgent.tech</div>
                <div className="text-slate-400">Support: support@nexgent.tech</div>
              </div>
            </div>

            <div className="p-3 bg-slate-950/40 rounded-lg border border-slate-800/80 text-center text-xs text-cyan-500 font-mono">
              {/* © {new Date().getFullYear()} Motherson Infotech / Nexgent. All rights reserved. */}
              Nexgent Trust & Governance Framework • Verified Profiles • Private Access • Privacy First

            </div>
          </section>

        </div>
      </main>
      <LandingFooter/>
    </div>
  );
};

export default PrivacyPolicy;